import { transferInstance } from '../utils/axiosConfig';

const TransferService = {

    // ── CRUD de base ──────────────────────────────────────────────────────────

    initTransfer: async (transferRequestDTO) => {
        try {
            const response = await transferInstance.post('/init', transferRequestDTO);
            return response.data;
        } catch (error) {
            console.error('Error in initTransfer:', error.message || error);
            throw error;
        }
    },

    confirmTransferWithRefNum: async (transferRequestDTO) => {
        try {
            const response = await transferInstance.post('/confirm', transferRequestDTO);
            return response.data;
        } catch (error) {
            console.error('Error in confirmTransferWithRefNum:', error.message || error);
            throw error;
        }
    },

    validateTransfer: async (transferRequestDTO) => {
        try {
            const response = await transferInstance.post('/validate', transferRequestDTO);
            return response.data;
        } catch (error) {
            console.error('Error in validateTransfer:', error.message || error);
            throw error;
        }
    },

    rejectTransfer: async (transferRequestDTO) => {
        try {
            const response = await transferInstance.post('/reject', transferRequestDTO);
            return response.data;
        } catch (error) {
            console.error('Error in rejectTransfer:', error.message || error);
            throw error;
        }
    },

    // ── Recherche / listing ───────────────────────────────────────────────────

    // Listing paginé avec filtre statut optionnel
    findAllTransfers: async (page = 0, size = 25, status = null) => {
        try {
            const params = new URLSearchParams({ page, size });
            if (status) params.append('status', status);
            const response = await transferInstance.get(`/findall?${params.toString()}`);
            return response.data;
        } catch (error) {
            console.error('Error in findAllTransfers:', error.message || error);
            throw error;
        }
    },

    findTransferById: async (id) => {
        try {
            const response = await transferInstance.get(`/find/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error in findTransferById:', error.message || error);
            throw error;
        }
    },

    findTransferByRefNum: async (refNum) => {
        try {
            const response = await transferInstance.get(`/findByRefNum/${refNum}`);
            return response.data;
        } catch (error) {
            console.error('Error in findTransferByRefNum:', error.message || error);
            throw error;
        }
    },

    updateTransfer: async (transferRequestDTO) => {
        try {
            const response = await transferInstance.put('/update', transferRequestDTO);
            return response.data;
        } catch (error) {
            console.error('Error in updateTransfer:', error.message || error);
            throw error;
        }
    },

    deleteTransfer: async (id) => {
        try {
            const response = await transferInstance.delete(`/delete/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error in deleteTransfer:', error.message || error);
            throw error;
        }
    },

    // Recherche multi-critères
    findTransferByVariousParameters: async (filters) => {
        const {
            issuerBankName    = '',
            country           = '',
            withdrawalMethod  = '',
            recipientId       = null,
            referenceNumber   = '',
            transactionAmount = null,
            dateDebut         = null,
            dateFin           = null,
        } = filters;
        try {
            const params = new URLSearchParams();
            if (issuerBankName)    params.append('issuerBankName',    issuerBankName);
            if (country)           params.append('country',           country);
            if (withdrawalMethod)  params.append('withdrawalMethod',  withdrawalMethod);
            if (recipientId)       params.append('recipientId',       recipientId);
            if (referenceNumber)   params.append('referenceNumber',   referenceNumber);
            if (transactionAmount) params.append('transactionAmount', transactionAmount);
            if (dateDebut)         params.append('dateDebut',         dateDebut);
            if (dateFin)           params.append('dateFin',           dateFin);

            const response = await transferInstance.get(`/findTransfer?${params.toString()}`);
            return response.data;
        } catch (error) {
            console.error('Error in findTransferByVariousParameters:', error.message || error);
            throw error;
        }
    },

    // Transferts par créateur avec dates optionnelles
    // → GET /findByCreatedBy/{createdBy}?startDate=…&endDate=…
    findTransfersByCreatedBy: async (createdBy, startDate = null, endDate = null) => {
        try {
            const params = new URLSearchParams();
            if (startDate) params.append('startDate', startDate);
            if (endDate)   params.append('endDate',   endDate);
            const query = params.toString() ? `?${params.toString()}` : '';
            const response = await transferInstance.get(
                `/findByCreatedBy/${encodeURIComponent(createdBy)}${query}`
            );
            return response.data;
        } catch (error) {
            console.error('Error in findTransfersByCreatedBy:', error.message || error);
            throw error;
        }
    },

    // Transferts par intervalle de dates (tous utilisateurs)
    // → GET /findByDateRange?startDate=…&endDate=…
    findTransfersByDateRange: async (startDate, endDate) => {
        try {
            const response = await transferInstance.get('/findByDateRange', {
                params: { startDate, endDate },
            });
            return response.data;
        } catch (error) {
            console.error('Error in findTransfersByDateRange:', error.message || error);
            throw error;
        }
    },

    // ── Export Excel ──────────────────────────────────────────────────────────

    // Export Excel par plage de dates (tous utilisateurs)
    // → GET /export/by-date-range?startDate=…&endDate=…
    exportExcelByDateRange: async (startDate, endDate) => {
        try {
            const response = await transferInstance.get('/export/by-date-range', {
                params: { startDate, endDate },
                responseType: 'arraybuffer',
            });
            return response;
        } catch (error) {
            console.error('Error in exportExcelByDateRange:', error.message || error);
            throw error;
        }
    },

    // Export Excel par utilisateur avec dates optionnelles
    // → GET /export/by-created-by/{createdBy}?startDate=…&endDate=…
    exportExcelByCreatedBy: async (createdBy, startDate = null, endDate = null) => {
        try {
            const params = new URLSearchParams();
            if (startDate) params.append('startDate', startDate);
            if (endDate)   params.append('endDate',   endDate);
            const query = params.toString() ? `?${params.toString()}` : '';
            const response = await transferInstance.get(
                `/export/by-created-by/${encodeURIComponent(createdBy)}${query}`,
                { responseType: 'arraybuffer' }
            );
            return response;
        } catch (error) {
            console.error('Error in exportExcelByCreatedBy:', error.message || error);
            throw error;
        }
    },

    // ── Export PDF ────────────────────────────────────────────────────────────

    // Export PDF par plage de dates (tous utilisateurs)
    // → GET /export-pdf/by-date-range?startDate=…&endDate=…
    exportPdfByDateRange: async (startDate, endDate) => {
        try {
            const response = await transferInstance.get('/export-pdf/by-date-range', {
                params: { startDate, endDate },
                responseType: 'arraybuffer',
            });
            return response;
        } catch (error) {
            console.error('Error in exportPdfByDateRange:', error.message || error);
            throw error;
        }
    },

    // Export PDF par utilisateur avec dates optionnelles
    // → GET /export-pdf/by-created-by/{createdBy}?startDate=…&endDate=…
    exportPdfByCreatedBy: async (createdBy, startDate = null, endDate = null) => {
        try {
            const params = new URLSearchParams();
            if (startDate) params.append('startDate', startDate);
            if (endDate)   params.append('endDate',   endDate);
            const query = params.toString() ? `?${params.toString()}` : '';
            const response = await transferInstance.get(
                `/export-pdf/by-created-by/${encodeURIComponent(createdBy)}${query}`,
                { responseType: 'arraybuffer' }
            );
            return response;
        } catch (error) {
            console.error('Error in exportPdfByCreatedBy:', error.message || error);
            throw error;
        }
    },

    // Export PDF détail d'un transfert spécifique
    // → GET /export-pdf/detail/{id}
    exportTransferDetailPdf: async (id) => {
        try {
            const response = await transferInstance.get(`/export-pdf/detail/${id}`, {
                responseType: 'arraybuffer',
            });
            return response;
        } catch (error) {
            console.error('Error in exportTransferDetailPdf:', error.message || error);
            throw error;
        }
    },

    // ── Utilitaire téléchargement (web) ───────────────────────────────────────
    downloadFile: (data, fileName, mimeType) => {
        const blob = data instanceof Blob
            ? data
            : new Blob([data], { type: mimeType });
        const url  = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href  = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    },
};

export default TransferService;